import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Box, Stack, Tabs, Tab, Badge } from '@mui/material';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import CorporateFareRoundedIcon from '@mui/icons-material/CorporateFareRounded';
import FolderSharedRoundedIcon from '@mui/icons-material/FolderSharedRounded';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    selectRequests, selectPendingApprovals, selectOrgDocs, selectEmployeeDocs, requestProgress,
} from '../redux/slices/documentsSlice';
import { selectEffectiveModules } from '../redux/slices/authSlice';
import { selectActiveEntityId } from '../redux/slices/orgSlice';
import { hasModule } from '../data/serverModules';
import http from '../Api/http';
import { GetDocumentRequests } from '../Api/Api';
import { PRIMARY } from '../theme';
import { card, PageHeader } from '../components/uiKit';
import DocumentRequestsTab from '../components/DocumentsComps/DocumentRequestsTab';
import DocumentApprovalsTab from '../components/DocumentsComps/DocumentApprovalsTab';
import OrgDocumentsTab from '../components/DocumentsComps/OrgDocumentsTab';
import EmployeeDocumentsTab from '../components/DocumentsComps/EmployeeDocumentsTab';

const TABS = [
    { key: 'requests', label: 'Requests', icon: AssignmentRoundedIcon, module: 'document-requests' },
    { key: 'approvals', label: 'Approvals', icon: FactCheckRoundedIcon, module: 'document-approvals' },
    { key: 'organisation', label: 'Organisation Documents', icon: CorporateFareRoundedIcon, module: 'organisation-documents' },
    { key: 'employee', label: 'Employee Documents', icon: FolderSharedRoundedIcon, module: 'employee-documents' },
];

export default function DocumentsPage() {
    const [params, setParams] = useSearchParams();
    const requests = useSelector(selectRequests);
    const pending = useSelector(selectPendingApprovals);
    const orgDocs = useSelector(selectOrgDocs);
    const employeeDocs = useSelector(selectEmployeeDocs);
    const modules = useSelector(selectEffectiveModules);

    const shownTabs = TABS.filter((t) => !t.module || hasModule(modules, t.module));
    const visibleTabs = shownTabs.length ? shownTabs : TABS.slice(0, 1);
    const tabKey = visibleTabs.some((t) => t.key === params.get('tab')) ? params.get('tab') : visibleTabs[0].key;
    const index = visibleTabs.findIndex((t) => t.key === tabKey);

    const activeEntityId = useSelector(selectActiveEntityId);

    const localStats = useMemo(() => {
        const open = requests.filter((r) => r.status === 'open');
        const awaited = open.reduce((n, r) => n + requestProgress(r).pending, 0);
        return { open: open.length, awaited, pending: pending.length, filed: employeeDocs.length, org: orgDocs.length };
    }, [requests, pending, employeeDocs, orgDocs]);

    const [summary, setSummary] = useState(null);
    const [, setSummaryLoading] = useState(true);
    const canReadSummary = hasModule(modules, 'document-requests');
    const loadSummary = useCallback(async () => {
        // Without the module there is no summary to wait for — fall straight
        // through to the local figures rather than skeletoning forever.
        if (!canReadSummary) { setSummaryLoading(false); return; }
        setSummaryLoading(true);
        try {
            const { data: body } = await http.get(GetDocumentRequests);
            if (!body?.error) setSummary(body?.data?.summary || null);
        } catch { } finally {
            setSummaryLoading(false);
        }
    }, [canReadSummary]);
    useEffect(() => { loadSummary(); }, [loadSummary, activeEntityId, tabKey]);

    // Switching entity invalidates the figures, so drop them and let the cards
    // skeleton again instead of showing the previous entity's numbers.
    useEffect(() => { setSummary(null); }, [activeEntityId]);

    const num = (v) => Number(v) || 0;
    const s = summary || {};
    const stats = {
        open: summary ? num(s.openRequests) : localStats.open,
        awaited: summary ? num(s.pendingUploads) : localStats.awaited,
        pending: summary ? num(s.awaitingApproval) : localStats.pending,
        filed: summary ? num(s.approvedFiled) : localStats.filed,
        org: summary ? num(s.organisationDocs) : localStats.org,
    };

    return (
        <Box sx={{ p: 2 }}>
            <PageHeader
                title="Document Management"
                subtitle="Request documents from employees, review what comes back, and keep shared company files in one place"
            />


            <Box sx={{ ...card, px: 1, mb: 1.5 }}>
                <Tabs
                    value={index}
                    onChange={(_, i) => setParams({ tab: visibleTabs[i].key })}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 52,
                        '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', bgcolor: PRIMARY },
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: 13.5, color: '#64748B', minHeight: 52, px: 2 },
                        '& .Mui-selected': { color: `${PRIMARY} !important` },
                    }}
                >
                    {visibleTabs.map((t) => (
                        <Tab
                            key={t.key}
                            iconPosition="start"
                            icon={
                                t.key === 'approvals' && stats.pending > 0
                                    ? (
                                        <Badge badgeContent={stats.pending} sx={{ '& .MuiBadge-badge': { bgcolor: '#E11D48', color: '#fff', fontSize: 9.5, fontWeight: 800, minWidth: 16, height: 16 } }}>
                                            <t.icon sx={{ fontSize: 18 }} />
                                        </Badge>
                                    )
                                    : <t.icon sx={{ fontSize: 18 }} />
                            }
                            label={t.label}
                        />
                    ))}
                </Tabs>
            </Box>

            <Stack spacing={1.5}>
                {tabKey === 'requests' && <DocumentRequestsTab />}
                {tabKey === 'approvals' && <DocumentApprovalsTab />}
                {tabKey === 'organisation' && <OrgDocumentsTab />}
                {tabKey === 'employee' && <EmployeeDocumentsTab />}
            </Stack>
        </Box>
    );
}
